import React, { useEffect, useRef } from 'react';

/**
 * Hybrid Fullscreen Interactive Background:
 * - Layer 1: Ambient Aurora Gradient Mesh with 3D mouse parallax
 * - Layer 2: Precision Coordinate Dot-Grid with Radial Cursor Spotlight
 * - Layer 3: Labeled Distributed Topology (Leader, Followers, Transformer Head, KV Cache, gRPC Gateway)
 *   plus Constellation Network with high-speed signal shooting star packets
 * - Fully theme-adaptive (dark/light) & high-DPI crisp rendering
 */
const InteractiveGraph = () => {
  const canvasRef = useRef(null);
  const animationIdRef = useRef(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let isRunning = true;
    let deviceScale = Math.min(window.devicePixelRatio || 1, 2);

    const isDark = () => document.documentElement.classList.contains('dark');

    // Sizing
    const resize = () => {
      const { innerWidth, innerHeight } = window;
      deviceScale = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.floor(innerWidth * deviceScale);
      canvas.height = Math.floor(innerHeight * deviceScale);
      canvas.style.width = `${innerWidth}px`;
      canvas.style.height = `${innerHeight}px`;
      ctx.setTransform(deviceScale, 0, 0, deviceScale, 0, 0);
      initScene(innerWidth, innerHeight);
    };

    // State
    const mouse = { x: window.innerWidth * 0.5, y: window.innerHeight * 0.5, active: false };
    let hasUserInteracted = false;

    const onMouseMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      mouse.x = e.clientX - rect.left;
      mouse.y = e.clientY - rect.top;
      mouse.active = true;
      hasUserInteracted = true;
    };

    const onMouseLeave = () => {
      mouse.active = false;
    };

    // Data structures
    let auroraOrbs = [];
    let topologyNodes = [];
    let topologyEdges = [];
    let constellationStars = [];
    let shootingPackets = [];

    const initScene = (w, h) => {
      // 1. Aurora Orbs (Mesh) - Soft & Deep Ambient
      auroraOrbs = [
        { x: w * 0.22, y: h * 0.35, vx: 0.3, vy: 0.2, baseR: 320, r: 320, phase: 0, darkColor: 'rgba(37, 99, 235, 0.12)', lightColor: 'rgba(59, 130, 246, 0.04)' },
        { x: w * 0.78, y: h * 0.40, vx: -0.25, vy: 0.3, baseR: 350, r: 350, phase: 2, darkColor: 'rgba(168, 85, 247, 0.10)', lightColor: 'rgba(168, 85, 247, 0.03)' },
        { x: w * 0.48, y: h * 0.78, vx: 0.2, vy: -0.25, baseR: 300, r: 300, phase: 4, darkColor: 'rgba(16, 185, 129, 0.09)', lightColor: 'rgba(16, 185, 129, 0.03)' },
        { x: w * 0.85, y: h * 0.82, vx: -0.2, vy: -0.2, baseR: 260, r: 260, phase: 1, darkColor: 'rgba(56, 189, 248, 0.08)', lightColor: 'rgba(56, 189, 248, 0.02)' }
      ];

      // 2. Labeled Distributed Topology Nodes
      topologyNodes = [
        // Left Cluster: Distributed Consensus
        { x: w * 0.16, y: h * 0.26, role: 'Leader Node', r: 7.5, color: '#3b82f6', isCore: true, pulse: 0 },
        { x: w * 0.08, y: h * 0.44, role: 'Follower A', r: 5, color: '#60a5fa', isCore: true, pulse: 1.2 },
        { x: w * 0.24, y: h * 0.48, role: 'Follower B', r: 5, color: '#60a5fa', isCore: true, pulse: 2.4 },
        { x: w * 0.14, y: h * 0.70, role: 'Candidate', r: 5, color: '#60a5fa', isCore: true, pulse: 3.6 },

        // Right Cluster: Neural ML Nodes
        { x: w * 0.86, y: h * 0.24, role: 'Transformer Head', r: 7.5, color: '#a855f7', isCore: true, pulse: 0.8 },
        { x: w * 0.74, y: h * 0.38, role: 'FeedForward', r: 5, color: '#c084fc', isCore: true, pulse: 2.0 },
        { x: w * 0.90, y: h * 0.54, role: 'KV Cache', r: 6, color: '#c084fc', isCore: true, pulse: 3.2 },
        { x: w * 0.78, y: h * 0.74, role: 'LoRA Adapter', r: 5, color: '#c084fc', isCore: true, pulse: 4.4 },

        // Center / Bridge Node
        { x: w * 0.50, y: h * 0.86, role: 'gRPC Gateway', r: 6.5, color: '#10b981', isCore: true, pulse: 1.6 }
      ];

      topologyEdges = [
        [0, 1], [0, 2], [1, 3], [2, 3], [0, 3],
        [4, 5], [4, 6], [5, 7], [6, 7],
        [3, 8], [7, 8]
      ];

      // 3. Ambient Constellation Stars (uncluttered density)
      const starCount = Math.max(18, Math.min(32, Math.floor((w * h) / 38000)));
      constellationStars = Array.from({ length: starCount }).map(() => ({
        x: Math.random() * w,
        y: Math.random() * h,
        vx: (Math.random() - 0.5) * 0.4,
        vy: (Math.random() - 0.5) * 0.4,
        r: 1.5 + Math.random() * 1.8,
        pulse: Math.random() * Math.PI * 2
      }));

      // 4. Shooting Data Packets along routes
      shootingPackets = [];
      for (let i = 0; i < 8; i++) {
        const edgeIdx = Math.floor(Math.random() * topologyEdges.length);
        shootingPackets.push({
          edgeIdx,
          progress: Math.random(),
          speed: 0.007 + Math.random() * 0.009,
          color: i % 2 === 0 ? '#38bdf8' : '#a855f7'
        });
      }
    };

    resize();
    window.addEventListener('resize', resize, { passive: true });
    window.addEventListener('mousemove', onMouseMove, { passive: true });
    window.addEventListener('mouseleave', onMouseLeave, { passive: true });

    // Render loop
    let frame = 0;
    const step = () => {
      if (!isRunning) return;
      frame++;

      const { innerWidth: w, innerHeight: h } = window;
      const dark = isDark();

      // Idle float for mouse when user hasn't moved
      if (!hasUserInteracted) {
        mouse.x = w * 0.5 + Math.sin(frame * 0.015) * (w * 0.22);
        mouse.y = h * 0.5 + Math.cos(frame * 0.012) * (h * 0.18);
      }

      ctx.clearRect(0, 0, w, h);

      // =========================================================
      // 1. LAYER 1: AMBIENT AURORA MESH WITH PARALLAX
      // =========================================================
      const parallaxX = (mouse.x - w / 2) * 0.035;
      const parallaxY = (mouse.y - h / 2) * 0.035;

      auroraOrbs.forEach(orb => {
        orb.x += orb.vx;
        orb.y += orb.vy;
        if (orb.x < -100 || orb.x > w + 100) orb.vx *= -1;
        if (orb.y < -100 || orb.y > h + 100) orb.vy *= -1;

        orb.phase += 0.015;
        orb.r = orb.baseR + Math.sin(orb.phase) * 35;

        const drawX = orb.x + parallaxX;
        const drawY = orb.y + parallaxY;
        const color = dark ? orb.darkColor : orb.lightColor;

        const grad = ctx.createRadialGradient(drawX, drawY, 0, drawX, drawY, orb.r);
        grad.addColorStop(0, color);
        grad.addColorStop(0.6, color.replace(/[\d.]+\)$/, dark ? '0.08)' : '0.03)'));
        grad.addColorStop(1, 'rgba(0, 0, 0, 0)');

        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(drawX, drawY, orb.r, 0, Math.PI * 2);
        ctx.fill();
      });

      // =========================================================
      // 2. LAYER 2: COORDINATE GRID & RADIAL SPOTLIGHT
      // =========================================================
      const gridSize = 46;
      ctx.fillStyle = dark ? 'rgba(148, 163, 184, 0.08)' : 'rgba(100, 116, 139, 0.12)';

      for (let x = 0; x < w; x += gridSize) {
        for (let y = 0; y < h; y += gridSize) {
          ctx.beginPath();
          ctx.arc(x, y, 1, 0, Math.PI * 2);
          ctx.fill();
        }
      }

      // Subtle Cursor Grid Revealer (Zero color wash/glare over text)
      if (mouse.active) {
        const spotRadius = 180;
        ctx.strokeStyle = dark ? 'rgba(59, 130, 246, 0.09)' : 'rgba(59, 130, 246, 0.06)';
        ctx.lineWidth = 0.8;
        const startX = Math.floor((mouse.x - spotRadius) / gridSize) * gridSize;
        const endX = Math.ceil((mouse.x + spotRadius) / gridSize) * gridSize;
        const startY = Math.floor((mouse.y - spotRadius) / gridSize) * gridSize;
        const endY = Math.ceil((mouse.y + spotRadius) / gridSize) * gridSize;

        for (let x = startX; x <= endX; x += gridSize) {
          if (x >= 0 && x <= w) {
            ctx.beginPath();
            ctx.moveTo(x, Math.max(0, startY));
            ctx.lineTo(x, Math.min(h, endY));
            ctx.stroke();
          }
        }
        for (let y = startY; y <= endY; y += gridSize) {
          if (y >= 0 && y <= h) {
            ctx.beginPath();
            ctx.moveTo(Math.max(0, startX), y);
            ctx.lineTo(Math.min(w, endX), y);
            ctx.stroke();
          }
        }
      }

      // =========================================================
      // 3. LAYER 3: CONSTELLATION NETWORK & STARS
      // =========================================================
      const maxStarDist = 130;
      for (let i = 0; i < constellationStars.length; i++) {
        for (let j = i + 1; j < constellationStars.length; j++) {
          const a = constellationStars[i];
          const b = constellationStars[j];
          const d = Math.hypot(a.x - b.x, a.y - b.y);
          if (d < maxStarDist) {
            const alpha = (1 - d / maxStarDist) * (dark ? 0.22 : 0.15);
            ctx.strokeStyle = dark ? `rgba(59, 130, 246, ${alpha})` : `rgba(37, 99, 235, ${alpha})`;
            ctx.lineWidth = 1;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        }
      }

      constellationStars.forEach(star => {
        star.x += star.vx;
        star.y += star.vy;
        if (star.x < 0 || star.x > w) star.vx *= -1;
        if (star.y < 0 || star.y > h) star.vy *= -1;

        star.pulse += 0.03;
        const currentR = star.r + Math.sin(star.pulse) * 0.4;

        ctx.fillStyle = dark ? 'rgba(96, 165, 250, 0.75)' : 'rgba(37, 99, 235, 0.65)';
        ctx.beginPath();
        ctx.arc(star.x, star.y, currentR, 0, Math.PI * 2);
        ctx.fill();
      });

      // =========================================================
      // 4. LAYER 4: LABELED DISTRIBUTED TOPOLOGY & PACKET SHOOTING STARS
      // =========================================================
      
      // Draw Topology Edges
      ctx.strokeStyle = dark ? 'rgba(71, 85, 105, 0.38)' : 'rgba(148, 163, 184, 0.45)';
      ctx.lineWidth = 1.3;
      topologyEdges.forEach(([i, j]) => {
        const a = topologyNodes[i];
        const b = topologyNodes[j];
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.lineTo(b.x, b.y);
        ctx.stroke();
      });

      // Packet Shooting Stars along routes
      shootingPackets.forEach(p => {
        p.progress += p.speed;
        if (p.progress > 1) {
          p.progress = 0;
          p.edgeIdx = Math.floor(Math.random() * topologyEdges.length);
        }
        const [i, j] = topologyEdges[p.edgeIdx];
        const a = topologyNodes[i];
        const b = topologyNodes[j];
        const px = a.x + (b.x - a.x) * p.progress;
        const py = a.y + (b.y - a.y) * p.progress;

        ctx.fillStyle = dark ? p.color : '#0284c7';
        ctx.shadowColor = p.color;
        ctx.shadowBlur = dark ? 10 : 4;
        ctx.beginPath();
        ctx.arc(px, py, 3, 0, Math.PI * 2);
        ctx.fill();
        ctx.shadowBlur = 0;
      });

      // Draw Labeled Nodes
      topologyNodes.forEach(node => {
        const distToMouse = Math.hypot(mouse.x - node.x, mouse.y - node.y);
        const isHovered = distToMouse < 55;

        // Core Node Fill
        ctx.fillStyle = node.color;
        ctx.shadowColor = node.color;
        ctx.shadowBlur = isHovered ? 18 : 6;
        ctx.beginPath();
        ctx.arc(node.x, node.y, isHovered ? node.r * 1.4 : node.r, 0, Math.PI * 2);
        ctx.fill();
        ctx.shadowBlur = 0;

        // Animated Consensus Ring
        node.pulse += 0.04;
        const ringR = node.r + Math.sin(node.pulse) * 3.5 + 3.5;
        ctx.strokeStyle = node.color + (dark ? '55' : '44');
        ctx.lineWidth = 1.2;
        ctx.beginPath();
        ctx.arc(node.x, node.y, ringR, 0, Math.PI * 2);
        ctx.stroke();

        // Node Label
        ctx.fillStyle = dark 
          ? (isHovered ? '#ffffff' : 'rgba(203, 213, 225, 0.85)')
          : (isHovered ? '#0f172a' : 'rgba(51, 65, 85, 0.9)');
        ctx.font = isHovered ? 'bold 11px monospace' : '10px monospace';
        ctx.fillText(node.role, node.x + 11, node.y + 3.5);
      });

      animationIdRef.current = requestAnimationFrame(step);
    };

    animationIdRef.current = requestAnimationFrame(step);

    // Theme toggle observer
    const observer = new MutationObserver(() => {
      // Re-trigger color adaptations
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });

    return () => {
      isRunning = false;
      cancelAnimationFrame(animationIdRef.current);
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseleave', onMouseLeave);
      observer.disconnect();
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 z-0 pointer-events-none select-none"
      aria-hidden="true"
    />
  );
};

export default InteractiveGraph;
